import { useContext, useMemo } from 'react'
import GeoContext from 'contexts/geoContext'
import Places, { getPlaceByRegionCode } from 'constants/places'
import useOfferList from './useOfferList'

export interface OfferListRequest {
  /** Full filters for this request — no destination or limit (those are set by the hook). */
  filters: Omit<App.OfferListFilters, 'destinationId' | 'limit'>
  limit: number
}

/**
 * Region-aware offer list fetcher, no carousel-specific filter logic — callers supply full
 * filters per request. `regionRequest` and `anywhereRequest` always fetch; `regionBackupRequest`
 * is optional and tops up the region result (capped at `regionRequest.limit`) when it falls
 * short. Region results are ordered first, then anywhere fills the rest.
 *
 * Fetches run in parallel: `excludedIds` is applied client-side only (see useOfferList), so it
 * never gates or re-triggers a request.
 *
 * Uses `destinationId`, not `placeIds`, for scoping — the hotel and tour search paths both key
 * off `destinationId`; `placeIds` is only read by the generic search branch neither offer type
 * routes through.
 */
export default function useRegionAwareOfferList(
  regionRequest: OfferListRequest,
  anywhereRequest: OfferListRequest,
  regionBackupRequest?: OfferListRequest,
): App.OfferList {
  const { currentRegionCode } = useContext(GeoContext)
  const regionPlaceId = useMemo(() => getPlaceByRegionCode(currentRegionCode).id, [currentRegionCode])

  const regionList = useOfferList({
    ...regionRequest.filters,
    destinationId: regionPlaceId,
    limit: regionRequest.limit,
  })

  const regionBackupList = useOfferList(
    regionBackupRequest && {
      ...regionBackupRequest.filters,
      destinationId: regionPlaceId,
      limit: regionBackupRequest.limit,
    },
    {
      disabled: !regionBackupRequest,
      excludedIds: regionList.offerIds,
    },
  )

  const regionCombinedIds = useMemo(() => (
    regionBackupRequest
      ? [...regionList.offerIds, ...regionBackupList.offerIds].slice(0, regionRequest.limit)
      : regionList.offerIds
  ), [regionBackupRequest, regionList.offerIds, regionBackupList.offerIds, regionRequest.limit])

  const anywhereList = useOfferList(
    {
      ...anywhereRequest.filters,
      destinationId: Places.Anywhere.id,
      limit: anywhereRequest.limit,
    },
    { excludedIds: regionCombinedIds },
  )

  return useMemo(() => ({
    ...anywhereList,
    offerIds: [...regionCombinedIds, ...anywhereList.offerIds],
    fetching: regionList.fetching || (!!regionBackupRequest && regionBackupList.fetching) || anywhereList.fetching,
  }), [regionCombinedIds, anywhereList, regionList.fetching, regionBackupList.fetching, regionBackupRequest])
}
